import type { PickerConfig } from "./api.js";

/** The one script from outside this app: Google's own, loaded only when someone opens the file picker. */
const GOOGLE_API = "https://apis.google.com/js/api.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    gapi?: { load: (name: string, cb: () => void) => void };
    google?: { picker?: any };
  }
}

let loading: Promise<void> | null = null;
export function loadGooglePicker(): Promise<void> {
  if (window.google?.picker) return Promise.resolve();
  loading ??= new Promise<void>((resolve, reject) => {
    const done = () => (window.gapi ? window.gapi.load("picker", () => resolve()) : reject(new Error("picker")));
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GOOGLE_API}"]`);
    if (existing) return existing.addEventListener("load", done, { once: true });
    const s = document.createElement("script");
    s.src = GOOGLE_API;
    s.async = true;
    s.onload = done;
    s.onerror = () => {
      loading = null;
      reject(new Error("picker"));
    };
    document.head.appendChild(s);
  });
  return loading;
}

export interface PickedFile {
  id: string;
  name: string;
  mimeType?: string;
}

/**
 * The size of the picker dialog: Google allows about 566x350 up to 1051x650. Left to itself the dialog can be taller than a laptop window,
 * with its title and search bar above the visible area, so it is sized from the window. On a phone Google lays it out itself.
 */
export function pickerSize(w: number, h: number): { width: number; height: number } | null {
  if (w < 600) return null;
  return { width: Math.max(566, Math.min(1051, w - 80)), height: Math.max(350, Math.min(650, h - 120)) };
}

/** Opens Google's file picker with the short-lived token; resolves with the files picked (none when closed). Folders cannot be picked. */
export async function pickDriveFiles(cfg: PickerConfig, locale: string): Promise<PickedFile[]> {
  await loadGooglePicker();
  const g = window.google!.picker;
  return new Promise<PickedFile[]>((resolve) => {
    // Folders are shown so people can open them and select the files inside (shift-click for a range); a folder itself cannot be
    // selected, because with the narrow permission it would not give Nemo the files inside it.
    const view = new g.DocsView(g.ViewId.DOCS).setIncludeFolders(true).setSelectFolderEnabled(false).setMode(g.DocsViewMode.LIST);
    let b = new g.PickerBuilder()
      .addView(view)
      .enableFeature(g.Feature.MULTISELECT_ENABLED)
      .setOAuthToken(cfg.accessToken)
      .setLocale(locale)
      .setCallback((data: any) => {
        if (data[g.Response.ACTION] === g.Action.PICKED) {
          const docs = (data[g.Response.DOCUMENTS] ?? []) as any[];
          resolve(docs.filter((d) => d.mimeType !== "application/vnd.google-apps.folder").map((d) => ({ id: String(d.id), name: String(d.name ?? d.id), mimeType: d.mimeType as string | undefined })));
        } else if (data[g.Response.ACTION] === g.Action.CANCEL) resolve([]);
      });
    // The picker checks the address of the page against the key's website restriction; tell it explicitly which page this is.
    b = b.setOrigin(`${window.location.protocol}//${window.location.host}`);
    const size = pickerSize(window.innerWidth, window.innerHeight);
    if (size) b = b.setSize(size.width, size.height);
    if (cfg.apiKey) b = b.setDeveloperKey(cfg.apiKey);
    if (cfg.appId) b = b.setAppId(cfg.appId);
    b.build().setVisible(true);
  });
}
